import { OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, EventEmitter, HostListener, Input, Output, TemplateRef, } from '@angular/core';
import { Subject } from 'rxjs';
import { ToggletipComponent } from './toggletip.component';
import * as i0 from "@angular/core";
import * as i1 from "@angular/cdk/overlay";
import * as i2 from "@angular/cdk/a11y";
let nextId = 0;
/**
 * @deprecated since version 9.0
 */
export class ToggletipDirective {
    constructor(elementRef, overlay, renderer, liveAnnouncer) {
        this.elementRef = elementRef;
        this.overlay = overlay;
        this.renderer = renderer;
        this.liveAnnouncer = liveAnnouncer;
        this.destroy$ = new Subject();
        this.toggletipWindowId = `mhe-toggletip-${nextId++}`;
        this.boundKeyDownFunc = this.onKeyDown.bind(this);
        this.placement = 'top';
        this.disableDefaultActions = false;
        this.toggleOnClick = true;
        this.panelClass = '';
        this.shown = new EventEmitter();
        this.hidden = new EventEmitter();
        this.toggleShown = new EventEmitter();
    }
    onClick() {
        if (!this.disableDefaultActions && this.toggleOnClick) {
            this.toggle();
        }
    }
    onFocus() {
        if (!this.disableDefaultActions && !this.toggleOnClick) {
            this.toggle();
        }
    }
    onBlur() {
        if (!this.disableDefaultActions) {
            this.close();
        }
    }
    onMouseover() {
        if (!this.disableDefaultActions && !this.toggleOnClick) {
            this.open();
        }
    }
    onmouseout() {
        if (!this.disableDefaultActions && !this.toggleOnClick) {
            this.close();
        }
    }
    ngAfterViewInit() {
        this.setAriaLabel();
    }
    close() {
        if (this.toggletipRef) {
            this.setAriaExpanded(false);
            this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
            this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-owns');
            this.toggletipRef.destroy();
            this.overlayRef.dispose();
            this.toggletipRef = null;
            window.removeEventListener('keydown', this.boundKeyDownFunc);
            this.hidden.emit('ToggletipDirective');
        }
    }
    isOpen() {
        return this.toggletipRef != null;
    }
    open() {
        if (this.toggleOnClick) {
            // UIK-345: only the toggle-tip should announce the body of the tip
            void this.liveAnnouncer.announce(this.toggletipMessage);
        }
        if (this.isOpen()) {
            return;
        }
        this.createToggletip();
        this.setAriaExpanded(true);
        this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-describedby', this.toggletipWindowId);
        this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-owns', this.toggletipWindowId);
        window.addEventListener('keydown', this.boundKeyDownFunc);
        this.shown.emit('ToggletipDirective');
        this.toggleShown.emit('ToggletipDirective');
    }
    toggle() {
        if (this.toggletipRef) {
            this.close();
        }
        else {
            this.open();
        }
    }
    createToggletip() {
        const overlayConfig = this.getOverlayConfig();
        this.overlayRef = this.overlay.create(overlayConfig);
        this.overlayRef.outsidePointerEvents().subscribe(() => this.close());
        const containerPortal = new ComponentPortal(ToggletipComponent);
        const componentRef = this.overlayRef.attach(containerPortal);
        const hostEl = this.elementRef;
        const toggletipEl = componentRef.instance.el;
        componentRef.instance.id = this.toggletipWindowId;
        componentRef.instance.placement = this.placement;
        componentRef.instance.toggleOnClick = this.toggleOnClick;
        componentRef.instance.afterViewInit.subscribe(() => {
            this.updatePositionStrategy(this.placement, hostEl, toggletipEl);
        });
        this.setToggletipMessage(componentRef, this.mheToggletip);
        this.updatePositionStrategy(this.placement, hostEl, toggletipEl);
        this.toggletipRef = componentRef;
    }
    getOverlayConfig() {
        const overlayConfig = new OverlayConfig({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            panelClass: this.panelClass,
        });
        return overlayConfig;
    }
    updatePositionStrategy(placement, hostEl, toggletipEl) {
        const position = this.getPosition(placement, hostEl, toggletipEl);
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions([position]);
        this.overlayRef.updatePositionStrategy(positionStrategy);
    }
    getPosition(placement, hostEl, toggletipEl) {
        if (placement === 'top') {
            return {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY: -15,
            };
        }
        else if (placement === 'bottom') {
            return {
                originX: 'center',
                originY: 'bottom',
                overlayX: 'center',
                overlayY: 'top',
            };
        }
        else if (placement === 'left') {
            const hostElHeight = Number(hostEl.nativeElement.scrollHeight);
            const toggletipHeight = Number(toggletipEl.nativeElement.scrollHeight);
            return {
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'bottom',
                offsetX: -15,
                offsetY: Math.round((hostElHeight + toggletipHeight) / 2),
            };
        }
        else if (placement === 'right') {
            const hostElHeight = Number(hostEl.nativeElement.scrollHeight);
            const toggletipHeight = Number(toggletipEl.nativeElement.scrollHeight);
            return {
                originX: 'end',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'bottom',
                offsetY: Math.round((hostElHeight + toggletipHeight) / 2),
            };
        }
        else {
            // Top is default
            return {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY: -15,
            };
        }
    }
    onPlacementChange(change) {
        if (this.toggletipRef && change) {
            this.toggletipRef.instance.placement = change.currentValue;
        }
    }
    onMessageChange(change) {
        if (this.toggletipRef && change) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.setToggletipMessage(this.toggletipRef, change.currentValue);
        }
    }
    setToggletipMessage(componentRef, message) {
        if (!componentRef) {
            return;
        }
        else if (this.mheToggletip instanceof TemplateRef) {
            componentRef.instance.templateRef = this.mheToggletip;
        }
        else if (typeof this.mheToggletip === 'string') {
            componentRef.instance.toggletipMessage = this.mheToggletip;
            this.toggletipMessage = this.mheToggletip;
        }
    }
    onKeyDown(event) {
        if (!this.disableDefaultActions && event.key === 'Escape') {
            this.close();
        }
    }
    setAriaExpanded(isExpanded) {
        if (this.toggleOnClick === false) {
            // only ToggleTip needs to set the expanded state for announce
            return;
        }
        if (this.elementRef.nativeElement.outerHTML.indexOf('<mhe-button') !== -1) {
            this.elementRef.nativeElement.firstElementChild.setAttribute('aria-expanded', isExpanded);
        }
        else {
            this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-expanded', isExpanded.toString());
        }
    }
    setAriaLabel() {
        let element;
        if (this.elementRef.nativeElement.outerHTML.indexOf('<mhe-button') !== -1) {
            element = this.elementRef.nativeElement.firstElementChild;
        }
        else {
            element = this.elementRef.nativeElement;
        }
        this.renderer.setAttribute(element, 'aria-label', `${element.textContent} toggle tip`);
    }
    ngOnChanges(changes) {
        this.onPlacementChange(changes.placement);
        this.onMessageChange(changes.mheToggletip);
    }
    ngOnDestroy() {
        this.close();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
ToggletipDirective.ɵfac = function ToggletipDirective_Factory(t) { return new (t || ToggletipDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.Overlay), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i2.LiveAnnouncer)); };
ToggletipDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ToggletipDirective, selectors: [["", "mheToggletip", ""]], hostBindings: function ToggletipDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function ToggletipDirective_click_HostBindingHandler() { return ctx.onClick(); })("focusin", function ToggletipDirective_focusin_HostBindingHandler() { return ctx.onFocus(); })("focusout", function ToggletipDirective_focusout_HostBindingHandler() { return ctx.onBlur(); })("mouseenter", function ToggletipDirective_mouseenter_HostBindingHandler() { return ctx.onMouseover(); })("mouseleave", function ToggletipDirective_mouseleave_HostBindingHandler() { return ctx.onmouseout(); });
    } }, inputs: { mheToggletip: "mheToggletip", placement: "placement", disableDefaultActions: "disableDefaultActions", toggleOnClick: "toggleOnClick", panelClass: "panelClass" }, outputs: { shown: "shown", hidden: "hidden", toggleShown: "toggleShown" }, exportAs: ["mheToggletip"], features: [i0.ɵɵNgOnChangesFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToggletipDirective, [{
        type: Directive,
        args: [{
                selector: '[mheToggletip]',
                exportAs: 'mheToggletip',
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i1.Overlay }, { type: i0.Renderer2 }, { type: i2.LiveAnnouncer }]; }, { mheToggletip: [{
            type: Input
        }], placement: [{
            type: Input
        }], disableDefaultActions: [{
            type: Input
        }], toggleOnClick: [{
            type: Input
        }], panelClass: [{
            type: Input
        }], shown: [{
            type: Output
        }], hidden: [{
            type: Output
        }], toggleShown: [{
            type: Output
        }], onClick: [{
            type: HostListener,
            args: ['click']
        }], onFocus: [{
            type: HostListener,
            args: ['focusin']
        }], onBlur: [{
            type: HostListener,
            args: ['focusout']
        }], onMouseover: [{
            type: HostListener,
            args: ['mouseenter']
        }], onmouseout: [{
            type: HostListener,
            args: ['mouseleave']
        }] }); })();
